import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, Clock, Users, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MobileStaff = () => {
  const { selectedProperty } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    if (selectedProperty) {
      fetchData();
    }
  }, [selectedProperty]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const [dashboardRes, tasksRes] = await Promise.all([
        axios.get(`${API}/mobile/staff/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/mobile/staff/tasks`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);

      setDashboard(dashboardRes.data);
      setTasks(tasksRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">Please select a property</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="mobile-staff-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Mobile Staff</h1>
          <p className="text-slate-600 mt-1">Mobile workflows for staff members</p>
        </div>
      </div>

      {!dashboard && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <Smartphone className="text-amber-600 mt-1" size={24} />
              <div>
                <h3 className="font-semibold text-amber-900 mb-2">Staff Account Not Linked</h3>
                <p className="text-sm text-amber-800">
                  To use mobile staff features, this user account needs to be linked to a staff member profile.
                  Contact your administrator to set up staff-user account linking.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Clock size={18} />
              Pending Tasks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">
              {dashboard?.pending_tasks || 0}
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <CheckCircle2 size={18} />
              Completed Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600">
              {dashboard?.completed_tasks || 0}
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Users size={18} />
              Role
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-purple-600">
              {dashboard?.role || 'Not Assigned'}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Mobile Features</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold mb-2">📱 Mobile Check-in/Check-out</h3>
              <p className="text-sm text-slate-600">
                Process guest arrivals and departures from mobile device with digital signatures
              </p>
            </div>

            <div className="p-4 bg-emerald-50 rounded-lg">
              <h3 className="font-semibold mb-2">🧹 Housekeeping Tasks</h3>
              <p className="text-sm text-slate-600">
                View and complete housekeeping assignments with photo documentation
              </p>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <h3 className="font-semibold mb-2">🔧 Maintenance Alerts</h3>
              <p className="text-sm text-slate-600">
                Receive and respond to maintenance requests with real-time updates
              </p>
            </div>

            <div className="p-4 bg-amber-50 rounded-lg">
              <h3 className="font-semibold mb-2">🔔 Push Notifications</h3>
              <p className="text-sm text-slate-600">
                Get instant alerts for new tasks, urgent requests, and shift updates
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>API Endpoints Available</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between p-2 bg-slate-50 rounded">
              <span className="font-mono text-slate-600">GET /mobile/staff/dashboard</span>
              <Badge>Ready</Badge>
            </div>
            <div className="flex justify-between p-2 bg-slate-50 rounded">
              <span className="font-mono text-slate-600">GET /mobile/staff/tasks</span>
              <Badge>Ready</Badge>
            </div>
            <div className="flex justify-between p-2 bg-slate-50 rounded">
              <span className="font-mono text-slate-600">POST /mobile/staff/check-in/:id</span>
              <Badge>Ready</Badge>
            </div>
            <div className="flex justify-between p-2 bg-slate-50 rounded">
              <span className="font-mono text-slate-600">GET /mobile/staff/housekeeping/assignments</span>
              <Badge>Ready</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MobileStaff;
